// §6.3 / §5.5 / §6.4 — Mission Control, and ⌘-Tab.
//
// §6.3 gives this skin a "Spaces strip + exposé", and that is exactly the arrangement: the strip of
// Spaces on top, the windows of the active Space spread below it. The other two skins put the
// desktop strip *under* the grid; here it leads, because on this platform Spaces are the subject
// and the windows are what is on them.
//
// The `switcher` mode is ⌘-Tab: opened while a modifier is held, advanced by pressing the chord
// again, committed when that modifier comes back up. **Which modifier is read from the keymap**,
// never hard-coded — this skin binds `Meta+Tab` where the others bind `Alt+Tab`, and the release
// handler follows the binding.
//
// Cancelling costs nothing in either mode: nothing is focused until it is committed.
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { COPY } from '@/copy';
import { useKernel, useStoreValue, useWindows } from '@/kernel/react';
import { modifiersOf } from '@/kernel/keyboard';
import { isGridKey, moveInGrid } from '@/kernel/wm/grid';
import { sortDesktops } from '@/kernel/wm/desktops';

/** How many windows a row of the exposé holds. */
export const EXPOSE_COLUMNS = 3;

export function Switcher() {
  const kernel = useKernel();
  const overlay = useStoreValue(kernel.shellui.store, (s) => s.overlay);
  const open = overlay === 'switcher' || overlay === 'overview';
  const cycling = overlay === 'switcher';
  const allWindows = useWindows();
  const [selected, setSelected] = useState(0);
  const surface = useRef<HTMLDivElement | null>(null);

  const activeDesktop = useStoreValue(kernel.wm.store, (s) => s.activeDesktopId);
  const desktopList = useStoreValue(kernel.wm.store, (s) => s.desktops);
  const desktops = useMemo(() => sortDesktops(desktopList), [desktopList]);

  // Most recently focused first: the window you want is nearly always the one you just left.
  const windows = useMemo(
    () =>
      allWindows
        .filter((win) => win.desktopId === activeDesktop)
        .slice()
        .sort((a, b) => b.focusedAt - a.focusedAt),
    [allWindows, activeDesktop],
  );

  useEffect(() => {
    if (!open) return;
    // ⌘-Tab's first press lands on the window you were *last* in, not the one you are in.
    setSelected(cycling ? 1 : 0);
    surface.current?.focus();
  }, [open, cycling]);

  const commit = useCallback(
    (index: number) => {
      const win = windows[index];
      kernel.shellui.closeOverlay();
      if (!win) return;
      if (win.state === 'minimized') kernel.wm.restore(win.id);
      kernel.wm.focus(win.id);
    },
    [kernel, windows],
  );

  const pending = useRef({ commit, selected });
  pending.current = { commit, selected };

  useEffect(() => {
    if (!cycling) return;
    return kernel.bus.on('shell:switcherAdvance', ({ delta }) => {
      setSelected((index) =>
        moveInGrid(index, windows.length, EXPOSE_COLUMNS, delta > 0 ? 'ArrowRight' : 'ArrowLeft'),
      );
    });
  }, [cycling, kernel, windows.length]);

  useEffect(() => {
    if (!cycling) return;
    const held = modifiersOf(kernel.keyboard.chordFor('switcher.windows') ?? '');
    if (held.length === 0) return;
    const onKeyUp = (event: KeyboardEvent): void => {
      if (!held.includes(event.key)) return;
      pending.current.commit(pending.current.selected);
    };
    document.addEventListener('keyup', onKeyUp);
    return () => document.removeEventListener('keyup', onKeyUp);
  }, [cycling, kernel]);

  const onKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      const key = event.key;
      if (isGridKey(key)) {
        event.preventDefault();
        setSelected((index) => moveInGrid(index, windows.length, EXPOSE_COLUMNS, key));
        return;
      }
      if (event.key === 'Enter') {
        event.preventDefault();
        commit(selected);
      }
      // Escape is the shell's, not the skin's.
    },
    [commit, selected, windows.length],
  );

  if (!open) return null;

  return (
    <div
      className="macos-mission"
      data-testid="switcher"
      data-mode={cycling ? 'cycle' : 'overview'}
      role="dialog"
      aria-label={cycling ? COPY.chrome.switchWindow : COPY.chrome.missionControl}
      tabIndex={-1}
      ref={surface}
      onKeyDown={onKeyDown}
      onClick={() => kernel.shellui.closeOverlay()}
    >
      {/* §5.5 — the Spaces strip, on top. Switching is a class toggle, never a DOM move. */}
      <div
        className="macos-spaces"
        role="tablist"
        aria-label={COPY.chrome.desktops}
        data-testid="switcher-desktops"
        onClick={(event) => event.stopPropagation()}
      >
        {desktops.map((desktop) => (
          <button
            key={desktop.id}
            type="button"
            role="tab"
            aria-selected={desktop.id === activeDesktop}
            className="macos-space"
            data-testid={`switcher-desktop-${desktop.id}`}
            data-active={String(desktop.id === activeDesktop)}
            // D-032 — a Space holding one fullscreen window reads differently from an ordinary one.
            data-fullscreen={String(kernel.desktops.isFullscreenSpace(desktop.id))}
            onClick={() => {
              kernel.desktops.setActive(desktop.id);
              setSelected(0);
            }}
          >
            {desktop.name}
          </button>
        ))}
        <button
          type="button"
          className="macos-space macos-space-add"
          aria-label={COPY.chrome.newDesktop}
          data-testid="switcher-desktop-new"
          onClick={() => kernel.desktops.create()}
        >
          +
        </button>
      </div>

      <div
        className="macos-expose"
        role="listbox"
        aria-label={COPY.chrome.openWindows}
        data-testid="switcher-grid"
        onClick={(event) => event.stopPropagation()}
      >
        {windows.map((win, index) => (
          <button
            key={win.id}
            type="button"
            role="option"
            aria-selected={index === selected}
            className="macos-expose-tile"
            data-testid={`switcher-tile-${win.id}`}
            data-selected={String(index === selected)}
            onPointerEnter={() => setSelected(index)}
            onClick={() => commit(index)}
          >
            <span className="macos-expose-thumb" aria-hidden="true" />
            <span className="macos-expose-label">{win.title}</span>
          </button>
        ))}
        {windows.length === 0 ? (
          <p className="macos-expose-empty" data-testid="switcher-empty">
            {COPY.chrome.noOpenWindows}
          </p>
        ) : null}
      </div>
    </div>
  );
}
